// ============================================================
//  ALTIMO Medical Center — apiClient.js
//  Ubicación sugerida: src/apiClient.js
//
//  Este archivo reemplaza la comunicación por IPC de Electron
//  (window.altimo.modulo.accion(datos)) por llamadas HTTP reales
//  al servidor Node (server.js) que ya armamos.
//
//  API_URL relativa ('/api'): como Express sirve el build en el
//  mismo puerto 4000, funciona en localhost, red local y
//  producción SIN recompilar. En desarrollo (npm start, puerto
//  3000) el "proxy" del package.json redirige al 4000.
// ============================================================

const API_URL = '/api';

// El token se guarda en memoria (variable normal de JS), NO en
// localStorage/sessionStorage — Electron tampoco los usaba, y
// además evita problemas si en el futuro esto corre dentro de
// un entorno que los restrinja.
let tokenActual = null;

// ── Renovación silenciosa del token ──────────────────────────
// El JWT expira (default 12h). Para que un turno largo no pierda
// la sesión a media operación, el token se renueva en segundo
// plano cada 4 horas de forma automática: cualquier petición que
// ocurra pasado ese intervalo dispara la renovación (sin bloquear
// la petición original). Si el usuario deja la PC inactiva hasta
// que el token expira, sí deberá hacer login de nuevo (correcto
// por seguridad).
const INTERVALO_RENOVACION_MS = 4 * 60 * 60 * 1000; // 4 horas
let tokenEmitidoEn = 0;
let renovando = false;

function setToken(token) {
  tokenActual = token;
  tokenEmitidoEn = Date.now();
}

function getToken() {
  return tokenActual;
}

async function renovarTokenSilencioso() {
  if (renovando || !tokenActual) return;
  renovando = true;
  try {
    const resp = await fetch(`${API_URL}/usuarios/renovar-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenActual}`,
      },
    });
    const json = await resp.json();
    if (resp.ok && json.ok && json.data?.token) {
      setToken(json.data.token);
    }
    // Si falla (token ya expirado, usuario desactivado), no se hace
    // nada: la siguiente petición protegida devolverá 401 y la app
    // pedirá login normal.
  } catch (_) {
    /* sin red: se reintenta en la próxima petición */
  } finally {
    renovando = false;
  }
}

/**
 * Hace una petición HTTP al servidor y devuelve SIEMPRE
 * { ok, data, error } — exactamente la misma forma que devolvía
 * window.altimo.modulo.accion(...) en la versión Electron, para
 * que el resto del código (los .jsx) no tenga que cambiar mucho.
 */
async function apiRequest(method, path, body) {
  // Renovación en segundo plano si el token ya tiene más de 4 horas.
  // No se espera (sin await): la petición actual sale con el token
  // vigente y el nuevo queda listo para las siguientes.
  if (tokenActual && Date.now() - tokenEmitidoEn > INTERVALO_RENOVACION_MS) {
    renovarTokenSilencioso();
  }

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (tokenActual) headers['Authorization'] = `Bearer ${tokenActual}`;

    const opciones = { method, headers };
    if (body !== undefined) opciones.body = JSON.stringify(body);

    const resp = await fetch(`${API_URL}${path}`, opciones);
    const json = await resp.json();

    if (!resp.ok || json.ok === false) {
      return { ok: false, error: json.error || `Error ${resp.status}` };
    }
    return { ok: true, data: json.data };
  } catch (e) {
    return { ok: false, error: 'No se pudo conectar con el servidor. Verifica tu conexión de red o que el servidor esté encendido.' };
  }
}

// ── API pública, organizada igual que window.altimo ──────────
// Así los componentes que ya existen necesitan el menor cambio posible:
// donde decía  window.altimo.usuarios.login(...)
// ahora dice   api.usuarios.login(...)
const api = {
  usuarios: {
    login: async (creds) => {
      const r = await apiRequest('POST', '/usuarios/login', creds);
      if (r.ok && r.data?.token) setToken(r.data.token);
      return r;
    },
    listar: () => apiRequest('GET', '/usuarios'),
    crear: (datos) => apiRequest('POST', '/usuarios', datos),
    editar: (datos) => apiRequest('PUT', `/usuarios/${datos.id}`, datos),
    cambiarPassword: ({ id, nuevaPassword }) => apiRequest('PUT', `/usuarios/${id}/password`, { nuevaPassword }),
    resetearPassword: (id) => apiRequest('PUT', `/usuarios/${id}/resetear-password`),
    toggleActivo: (id, activo) => apiRequest('PUT', `/usuarios/${id}/toggle-activo`, { activo }),
    actividad: (id, limite) => apiRequest('GET', `/usuarios/${id}/actividad?limite=${limite || 50}`),
    // Antes 'usuarios:subir-firma'. Sube la imagen y el servidor devuelve
    // la ruta pública (ej. "/archivos/firmas/..."); úsala directo en <img src>.
    subirFirma: ({ usuario_id, tipo, fileData, fileExt }) => apiRequest('POST', `/usuarios/${usuario_id}/firma`, { tipo, fileData, fileExt }),
  },

  dashboard: {
    kpis: () => apiRequest('GET', '/dashboard/kpis'),
    pacientesActivos: () => apiRequest('GET', '/dashboard/pacientes-activos'),
    medicoPacientes: (medico_id) => apiRequest('GET', `/dashboard/medico-pacientes?medico_id=${medico_id}`),
    medicoLabsPendientes: (medico_id) => apiRequest('GET', `/dashboard/medico-labs-pendientes?medico_id=${medico_id}`),
    medicoMarcarLabRevisado: (datos) => apiRequest('PUT', '/dashboard/medico-marcar-lab-revisado', datos),
    medicoPrescripcionesPendientes: (medico_id) => apiRequest('GET', `/dashboard/medico-prescripciones-pendientes?medico_id=${medico_id}`),
  },

  pacientes: {
    buscar: (texto) => apiRequest('GET', `/pacientes/buscar?texto=${encodeURIComponent(texto || '')}`),
    buscarFormularios: (texto) => apiRequest('GET', `/pacientes/buscar-formularios?texto=${encodeURIComponent(texto || '')}`),
    obtenerFormularios: (id) => apiRequest('GET', `/pacientes/${id}/formularios`),
    obtener: (id) => apiRequest('GET', `/pacientes/${id}`),
    crear: (datos) => apiRequest('POST', '/pacientes', datos),
    editar: (datos) => apiRequest('PUT', `/pacientes/${datos.id}`, datos),
  },

  admisiones: {
    crear: (datos) => apiRequest('POST', '/admisiones', datos),
    obtener: (id) => apiRequest('GET', `/admisiones/${id}`),
    buscar: (texto) => apiRequest('GET', `/admisiones/buscar?texto=${encodeURIComponent(texto || '')}`),
    darAlta: (id, datos) => apiRequest('PUT', `/admisiones/${id}/dar-alta`, datos),
    guardarPdfDisco: ({ admision_id, html }) => apiRequest('POST', `/admisiones/${admision_id}/pdf`, { html }),
  },

  global: {
    buscar: (texto) => apiRequest('GET', `/global/buscar?texto=${encodeURIComponent(texto || '')}`),
  },

  cuenta: {
    items: (admision_id) => apiRequest('GET', `/cuenta/${admision_id}/items`),
    total: (admision_id) => apiRequest('GET', `/cuenta/${admision_id}/total`),
    agregarItem: (datos) => apiRequest('POST', '/cuenta/agregar-item', datos),
    anularItem: (id, datos) => apiRequest('PUT', `/cuenta/anular-item/${id}`, datos),
    cerrar: (datos) => apiRequest('PUT', '/cuenta/cerrar', datos),
    reabrir: (datos) => apiRequest('PUT', '/cuenta/reabrir', datos),
  },

  inventario: {
    listar: () => apiRequest('GET', '/inventario'),
    buscar: (texto) => apiRequest('GET', `/inventario/buscar?texto=${encodeURIComponent(texto || '')}`),
    productoDetalle: (id) => apiRequest('GET', `/inventario/${id}`),
    crear: (datos) => apiRequest('POST', '/inventario', datos),
    editar: (datos) => apiRequest('PUT', `/inventario/${datos.id}`, datos),
    entrada: (datos) => apiRequest('POST', `/inventario/${datos.id}/entrada`, datos),
    lotesProducto: (inventario_id) => apiRequest('GET', `/inventario/${inventario_id}/lotes`),
    ajuste: (datos) => apiRequest('POST', '/inventario/ajuste', datos),
    kardex: ({ inventario_id, desde, hasta, limite }) => {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      if (limite) params.set('limite', limite);
      return apiRequest('GET', `/inventario/${inventario_id}/kardex?${params.toString()}`);
    },
  },

  notificaciones: {
    crear: (datos) => apiRequest('POST', '/notificaciones', datos),
    listar: ({ area, rol, solo_no_leidas, limite } = {}) => {
      const params = new URLSearchParams();
      if (area) params.set('area', area);
      if (rol) params.set('rol', rol);
      if (solo_no_leidas) params.set('solo_no_leidas', 'true');
      if (limite) params.set('limite', limite);
      return apiRequest('GET', `/notificaciones?${params.toString()}`);
    },
    contarNoLeidas: ({ area, rol } = {}) => {
      const params = new URLSearchParams();
      if (area) params.set('area', area);
      if (rol) params.set('rol', rol);
      return apiRequest('GET', `/notificaciones/contar-no-leidas?${params.toString()}`);
    },
    marcarLeida: ({ id }) => apiRequest('PUT', `/notificaciones/${id}/marcar-leida`),
    marcarTodasLeidas: ({ area, rol }) => apiRequest('PUT', '/notificaciones/marcar-todas', { area, rol }),
  },

  indicacionesEnf: {
    crear: (datos) => apiRequest('POST', '/indicaciones-enf', datos),
    listar: ({ admision_id, estado } = {}) => {
      const params = new URLSearchParams();
      if (admision_id) params.set('admision_id', admision_id);
      if (estado) params.set('estado', estado);
      return apiRequest('GET', `/indicaciones-enf?${params.toString()}`);
    },
    pendientes: () => apiRequest('GET', '/indicaciones-enf/pendientes'),
    // v_estaciones: ahora acepta estacion_id + consumos[] opcionales
    // (insumos de estacion_stock usados para cumplir la indicación,
    // ej. guantes, pañal, kit de canalización). Si vienen, el backend
    // resta stock de la estación Y genera el cargo automáticamente.
    cumplir: ({ id, usuario_id, observacion, estacion_id, consumos }) =>
      apiRequest('PUT', `/indicaciones-enf/${id}/cumplir`, { usuario_id, observacion, estacion_id, consumos }),
    cancelar: ({ id, motivo, usuario_id, usuario_rol }) => apiRequest('PUT', `/indicaciones-enf/${id}/cancelar`, { motivo, usuario_id, usuario_rol }),
  },

  ordenesLab: {
    crear: (datos) => apiRequest('POST', '/ordenes-lab', datos),
    listar: ({ admision_id, estado } = {}) => {
      const params = new URLSearchParams();
      if (admision_id) params.set('admision_id', admision_id);
      if (estado) params.set('estado', estado);
      return apiRequest('GET', `/ordenes-lab?${params.toString()}`);
    },
    pendientes: () => apiRequest('GET', '/ordenes-lab/pendientes'),
    tomar: ({ id, usuario_id }) => apiRequest('PUT', `/ordenes-lab/${id}/tomar`, { usuario_id }),
    cancelar: ({ id, motivo, usuario_id }) => apiRequest('PUT', `/ordenes-lab/${id}/cancelar`, { motivo, usuario_id }),
  },

  lab: {
    subir: (datos) => apiRequest('POST', '/lab/subir', datos),
    listar: (admision_id) => apiRequest('GET', `/lab?admision_id=${admision_id}`),
    resultadosHoy: () => apiRequest('GET', '/lab/resultados-hoy'),
    eliminar: ({ id }) => apiRequest('DELETE', `/lab/${id}`),
    // Antes 'lab:abrir' usaba shell.openPath() en Electron. Ahora no hay
    // función que "abra" el archivo — el componente debe armar la URL
    // completa con esta función y usarla en un <a href> o window.open().
    urlArchivo: (rutaRelativa) => `${API_URL.replace('/api', '')}${rutaRelativa}${rutaRelativa.includes('?') ? '&' : '?'}t=${getToken()}`,
  },

  reportes: {
    dia: ({ fecha } = {}) => apiRequest('GET', `/reportes/dia?fecha=${fecha || ''}`),
    mes: ({ anio, mes }) => apiRequest('GET', `/reportes/mes?anio=${anio}&mes=${mes}`),
    semana: () => apiRequest('GET', '/reportes/semana'),
    alertas: () => apiRequest('GET', '/reportes/alertas'),
    facturacionRango: ({ desde, hasta }) => apiRequest('GET', `/reportes/facturacion-rango?desde=${desde}&hasta=${hasta}`),
    cargosRango: ({ desde, hasta }) => apiRequest('GET', `/reportes/cargos-rango?desde=${desde}&hasta=${hasta}`),
    inventarioValor: () => apiRequest('GET', '/reportes/inventario-valor'),
    kardexRango: ({ inventario_id, desde, hasta }) => {
      const params = new URLSearchParams({ inventario_id });
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      return apiRequest('GET', `/reportes/kardex-rango?${params.toString()}`);
    },
    controlados: ({ desde, hasta }) => apiRequest('GET', `/reportes/controlados?desde=${desde}&hasta=${hasta}`),
    cajaDia: ({ fecha } = {}) => apiRequest('GET', `/reportes/caja-dia?fecha=${fecha || ''}`),
    libroVentas: ({ desde, hasta }) => apiRequest('GET', `/reportes/libro-ventas?desde=${desde}&hasta=${hasta}`),
    cuentasPorCobrar: () => apiRequest('GET', '/reportes/cuentas-por-cobrar'),
    anulaciones: ({ desde, hasta }) => apiRequest('GET', `/reportes/anulaciones?desde=${desde}&hasta=${hasta}`),
    resumenMensual: ({ anio } = {}) => apiRequest('GET', `/reportes/resumen-mensual?anio=${anio || ''}`),
    gerencialMedicos: ({ desde, hasta }) => apiRequest('GET', `/reportes/gerencial-medicos?desde=${desde}&hasta=${hasta}`),
    gerencialCirugias: ({ desde, hasta }) => apiRequest('GET', `/reportes/gerencial-cirugias?desde=${desde}&hasta=${hasta}`),
    gerencialRentabilidad: ({ desde, hasta }) => apiRequest('GET', `/reportes/gerencial-rentabilidad?desde=${desde}&hasta=${hasta}`),
    gerencialOcupacion: ({ desde, hasta }) => apiRequest('GET', `/reportes/gerencial-ocupacion?desde=${desde}&hasta=${hasta}`),
    gerencialTopServicios: ({ desde, hasta, tipo }) => {
      const params = new URLSearchParams({ desde, hasta });
      if (tipo) params.set('tipo', tipo);
      return apiRequest('GET', `/reportes/gerencial-top-servicios?${params.toString()}`);
    },
  },

  prescripciones: {
    crear: (datos) => apiRequest('POST', '/prescripciones', datos),
    listar: ({ admision_id, estado } = {}) => {
      const params = new URLSearchParams();
      if (admision_id) params.set('admision_id', admision_id);
      if (estado) params.set('estado', estado);
      return apiRequest('GET', `/prescripciones?${params.toString()}`);
    },
    // Solo productos con distribucion_directa=1 (bombas/insumos
    // específicos): Farmacia dispensa directo al paciente.
    pendientesFarmacia: () => apiRequest('GET', '/prescripciones/pendientes-farmacia'),
    dispensar: ({ id, usuario_id, cantidad_dispensada }) => apiRequest('PUT', `/prescripciones/${id}/dispensar`, { usuario_id, cantidad_dispensada }),
    cancelar: ({ id, motivo, usuario_id }) => apiRequest('PUT', `/prescripciones/${id}/cancelar`, { motivo, usuario_id }),

    // ── v_estaciones: flujo vía Estación de Enfermería ──────────
    // Productos normales (distribucion_directa=0) aún sin transferir
    // a ninguna estación. Farmacia las atiende con estaciones.transferir().
    pendientesEstacion: () => apiRequest('GET', '/prescripciones/pendientes-estacion'),
    // Prescripciones ya transferidas a una estación (estado
    // 'Transferido_Estacion'), listas para que enfermería administre.
    enEstacion: ({ estacion_id, admision_id } = {}) => {
      const params = new URLSearchParams();
      if (estacion_id) params.set('estacion_id', estacion_id);
      if (admision_id) params.set('admision_id', admision_id);
      return apiRequest('GET', `/prescripciones/en-estacion?${params.toString()}`);
    },
    // Enfermería registra UNA dosis desde la estación. NO genera
    // cargo en el acto — el cobro real ocurre al consolidar
    // (api.estaciones.consolidar), que agrupa todas las dosis
    // pendientes por producto. Puede llamarse varias veces para
    // la misma prescripción, con cantidades distintas cada vez.
    administrarEnEstacion: (id, { usuario_id, cantidad_administrada }) =>
      apiRequest('PUT', `/prescripciones/${id}/administrar-en-estacion`, { usuario_id, cantidad_administrada }),
  },

  // ── v_estaciones (v2): Estaciones de Enfermería (piso/área) ──
  // Modelo de consumo acumulado + cobro consolidado:
  // Farmacia ──(transferir)──▶ Estación
  // Estación ──(administrar, NO cobra)──▶ Paciente   [N veces, cantidades variables]
  // Estación ──(consolidar: agrupa y cobra)──▶ Cuenta del paciente
  // Estación ──(devolverSobrante, NO cobra)──▶ Farmacia
  estaciones: {
    listar: () => apiRequest('GET', '/estaciones'),
    crear: (datos) => apiRequest('POST', '/estaciones', datos),
    editar: (datos) => apiRequest('PUT', `/estaciones/${datos.id}`, datos),
    asignarHabitacion: (habitacion_id, { estacion_id }) =>
      apiRequest('PUT', `/estaciones/habitacion/${habitacion_id}/asignar`, { estacion_id }),

    stock: (id) => apiRequest('GET', `/estaciones/${id}/stock`),
    movimientos: (id) => apiRequest('GET', `/estaciones/${id}/movimientos`),
    stockCritico: (id) => apiRequest('GET', `/estaciones/${id}/stock-critico`),

    // Farmacia transfiere a una estación (con o sin prescripcion_id).
    transferir: (id, { inventario_id, cantidad, usuario_id, prescripcion_id }) =>
      apiRequest('POST', `/estaciones/${id}/transferir`, { inventario_id, cantidad, usuario_id, prescripcion_id }),

    // Enfermería REGISTRA una dosis/consumo. NO genera cargo todavía
    // — eso ocurre al consolidar. Puede llamarse cualquier número de
    // veces para el mismo paciente/producto, con cantidades distintas.
    administrar: (id, { inventario_id, admision_id, cantidad, usuario_id, prescripcion_id }) =>
      apiRequest('POST', `/estaciones/${id}/administrar`, {
        inventario_id, admision_id, cantidad, usuario_id, prescripcion_id,
      }),

    // Agrupa TODOS los consumos pendientes de una admisión por
    // producto y genera un cargo único por cada uno. Manual (botón)
    // o se ejecuta automáticamente al cerrar la cuenta del paciente.
    consolidar: ({ admision_id, usuario_id }) =>
      apiRequest('POST', '/estaciones/consolidar', { admision_id, usuario_id }),

    // Vista previa: qué se cobraría si se consolida esta admisión ahora.
    pendientesConsolidar: (admision_id) =>
      apiRequest('GET', `/estaciones/pendientes-consolidar?admision_id=${admision_id}`),

    // Detalle de auditoría: todas las dosis individuales (fecha, hora,
    // usuario, cantidad, facturada o no) de un cargo o de un producto
    // en una admisión específica.
    detalleConsumo: ({ cuenta_item_id, admision_id, inventario_id } = {}) => {
      const params = new URLSearchParams();
      if (cuenta_item_id) params.set('cuenta_item_id', cuenta_item_id);
      if (admision_id)    params.set('admision_id', admision_id);
      if (inventario_id)  params.set('inventario_id', inventario_id);
      return apiRequest('GET', `/estaciones/detalle-consumo?${params.toString()}`);
    },

    // Sobrante no usado en el paciente: regresa a Farmacia, nunca se cobró.
    devolverSobrante: (id, { inventario_id, cantidad, usuario_id, motivo }) =>
      apiRequest('POST', `/estaciones/${id}/devolver-sobrante`, { inventario_id, cantidad, usuario_id, motivo }),

    // Reposición de botiquín fijo (analgésicos, guantes, pañales, kits...).
    reponer: (id, { items, usuario_id }) =>
      apiRequest('POST', `/estaciones/${id}/reponer`, { items, usuario_id }),
  },

  devoluciones: {
    solicitar: (datos) => apiRequest('POST', '/devoluciones/solicitar', datos),
    pendientesFarmacia: () => apiRequest('GET', '/devoluciones/pendientes-farmacia'),
    listar: (admision_id) => apiRequest('GET', `/devoluciones?admision_id=${admision_id || ''}`),
    confirmar: ({ id, cantidad_confirmada, observacion_farmacia, usuario_id }) =>
      apiRequest('PUT', `/devoluciones/${id}/confirmar`, { cantidad_confirmada, observacion_farmacia, usuario_id }),
    rechazar: ({ id, observacion_farmacia, usuario_id }) =>
      apiRequest('PUT', `/devoluciones/${id}/rechazar`, { observacion_farmacia, usuario_id }),
  },

  tarifario: {
    listar: () => apiRequest('GET', '/tarifario'),
    buscar: (texto) => apiRequest('GET', `/tarifario/buscar?texto=${encodeURIComponent(texto || '')}`),
    crear: (datos) => apiRequest('POST', '/tarifario', datos),
    editar: (datos) => apiRequest('PUT', `/tarifario/${datos.id}`, datos),
  },

  activosFijos: {
    listar: (filtros = {}) => {
      const qs = new URLSearchParams(filtros).toString();
      return apiRequest('GET', `/activos-fijos${qs ? '?' + qs : ''}`);
    },
    obtener: (id) => apiRequest('GET', `/activos-fijos/${id}`),
    crear: (datos) => apiRequest('POST', '/activos-fijos', datos),
    editar: (datos) => apiRequest('PUT', `/activos-fijos/${datos.id}`, datos),
    movimiento: (id, datos) => apiRequest('POST', `/activos-fijos/${id}/movimiento`, datos),
    baja: (id, datos) => apiRequest('POST', `/activos-fijos/${id}/baja`, datos),
    cerrarPeriodo: (datos) => apiRequest('POST', '/activos-fijos/cerrar-periodo', datos),
    reporteDepreciacion: (periodo) =>
      apiRequest('GET', `/activos-fijos/reportes/depreciacion-mensual${periodo ? '?periodo=' + periodo : ''}`),
  },


  facturas: {
    generar: (datos) => apiRequest('POST', '/facturas/generar', datos),
    obtener: (admision_id) => apiRequest('GET', `/facturas/${admision_id}`),
    itemsPdf: (admision_id) => apiRequest('GET', `/facturas/${admision_id}/items-pdf`),
    guardarPdfDisco: ({ admision_id, html }) => apiRequest('POST', `/facturas/${admision_id}/pdf`, { html }),
  },

  pagos: {
    registrar: (datos) => apiRequest('POST', '/facturas/pagos', datos),
  },

  cirugias: {
    listar: ({ fecha, quirofano_id }) => {
      const params = new URLSearchParams({ fecha });
      if (quirofano_id) params.set('quirofano_id', quirofano_id);
      return apiRequest('GET', `/cirugias?${params.toString()}`);
    },
    crear: (datos) => apiRequest('POST', '/cirugias', datos),
    reprogramar: ({ id, hora_nueva, motivo, usuario_id, usuario_nombre }) =>
      apiRequest('PUT', `/cirugias/${id}/reprogramar`, { hora_nueva, motivo, usuario_id, usuario_nombre }),
    finalizar: ({ id, hora_fin, usuario_id }) => apiRequest('PUT', `/cirugias/${id}/finalizar`, { hora_fin, usuario_id }),
    agregarObs: ({ id, texto, usuario_id, usuario_nombre }) =>
      apiRequest('PUT', `/cirugias/${id}/agregar-obs`, { texto, usuario_id, usuario_nombre }),
    reporte: ({ desde, hasta, quirofano_id }) => {
      const params = new URLSearchParams({ desde, hasta });
      if (quirofano_id) params.set('quirofano_id', quirofano_id);
      return apiRequest('GET', `/cirugias/reporte?${params.toString()}`);
    },
    eliminar: (id) => apiRequest('DELETE', `/cirugias/${id}`),
  },

  formularios: {
    guardar: (datos) => apiRequest('POST', '/formularios', datos),
    cargar: ({ admision_id, formulario_id }) => apiRequest('GET', `/formularios/${admision_id}/${formulario_id}`),
    listarAdmision: (admision_id) => apiRequest('GET', `/formularios/admision/${admision_id}`),
    guardarPdfDisco: ({ admision_id, formulario_id, html }) =>
      apiRequest('POST', `/formularios/${admision_id}/${formulario_id}/pdf`, { html }),
    abrirPdf: ({ admision_id, formulario_id }) => apiRequest('GET', `/formularios/${admision_id}/${formulario_id}/pdf`),
  },

  expedientes: {
    listar: ({ anio, mes } = {}) => {
      const params = new URLSearchParams();
      if (anio) params.set('anio', anio);
      if (mes) params.set('mes', mes);
      return apiRequest('GET', `/expedientes?${params.toString()}`);
    },
    obtenerPaciente: (paciente_id) => apiRequest('GET', `/expedientes/paciente/${paciente_id}`),
    historial: (paciente_id) => apiRequest('GET', `/expedientes/paciente/${paciente_id}/historial`),
    alergias: (admision_id) => apiRequest('GET', `/expedientes/admision/${admision_id}/alergias`),
    generarPdfFoleado: (admision_id) => apiRequest('POST', `/expedientes/${admision_id}/pdf-foleado`),
    abrirPdfFoleado: (rutaPath) => apiRequest('GET', `/expedientes/pdf-foleado?path=${encodeURIComponent(rutaPath)}`),
  },

  medicos: {
    listar: () => apiRequest('GET', '/medicos'),
    crear: (datos) => apiRequest('POST', '/medicos', datos),
    editar: (datos) => apiRequest('PUT', `/medicos/${datos.id}`, datos),
    toggle: ({ id, activo }) => apiRequest('PUT', `/medicos/${id}/toggle`, { activo }),
  },

  medicosRemitentes: {
    listar: () => apiRequest('GET', '/medicos-remitentes'),
    guardar: (datos) => apiRequest('POST', '/medicos-remitentes', datos),
    eliminar: (id) => apiRequest('DELETE', `/medicos-remitentes/${id}`),
  },

  habitaciones: {
    listar: () => apiRequest('GET', '/habitaciones'),
    cambiarEstado: ({ id, estado, uso_otro }) => apiRequest('PUT', `/habitaciones/${id}/cambiar-estado`, { estado, uso_otro }),
    crear: (datos) => apiRequest('POST', '/habitaciones', datos),
    editar: (datos) => apiRequest('PUT', `/habitaciones/${datos.id}`, datos),
  },

  clinicas: {
    listar: () => apiRequest('GET', '/clinicas'),
    crear: (datos) => apiRequest('POST', '/clinicas', datos),
    editar: (datos) => apiRequest('PUT', `/clinicas/${datos.id}`, datos),
    cambiarEstado: ({ id, estado }) => apiRequest('PUT', `/clinicas/${id}/cambiar-estado`, { estado }),
    eliminar: (id) => apiRequest('DELETE', `/clinicas/${id}`),
  },

  audit: {
    registrar: (datos) => apiRequest('POST', '/audit', datos),
    listar: ({ tabla, registro_id, limite } = {}) => {
      const params = new URLSearchParams();
      if (tabla) params.set('tabla', tabla);
      if (registro_id) params.set('registro_id', registro_id);
      if (limite) params.set('limite', limite);
      return apiRequest('GET', `/audit?${params.toString()}`);
    },
  },

  notas: {
    guardar: (datos) => apiRequest('POST', '/notas', datos),
    listar: ({ paciente_id, admision_id } = {}) => {
      const params = new URLSearchParams({ paciente_id });
      if (admision_id) params.set('admision_id', admision_id);
      return apiRequest('GET', `/notas?${params.toString()}`);
    },
    leerImagenFirma: ({ usuario_id }) => apiRequest('GET', `/notas/firma/${usuario_id}`),
  },

  turnos: {
    // Catálogo
    catalogo: () => apiRequest('GET', '/turnos/catalogo'),
    crearTurnoCatalogo: (datos) => apiRequest('POST', '/turnos/catalogo', datos),
    editarTurnoCatalogo: (id, datos) => apiRequest('PUT', `/turnos/catalogo/${id}`, datos),
    // Personal del área (para la grilla)
    personal: (area) => apiRequest('GET', `/turnos/personal?area=${area}`),
    // Programación
    asignaciones: ({ desde, hasta, area }) =>
      apiRequest('GET', `/turnos/asignaciones?desde=${desde}&hasta=${hasta}&area=${area}`),
    asignar: (datos) => apiRequest('POST', '/turnos/asignaciones', datos),
    editarAsignacion: (id, datos) => apiRequest('PUT', `/turnos/asignaciones/${id}`, datos),
    eliminarAsignacion: (id) => apiRequest('DELETE', `/turnos/asignaciones/${id}`),
    miSemana: ({ desde, hasta }) => apiRequest('GET', `/turnos/mi-semana?desde=${desde}&hasta=${hasta}`),
    // Entrega / recepción de turno
    entregas: ({ desde, hasta, area } = {}) => {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      if (area) params.set('area', area);
      return apiRequest('GET', `/turnos/entregas?${params.toString()}`);
    },
    entregaDetalle: (id) => apiRequest('GET', `/turnos/entregas/${id}`),
    crearEntrega: (datos) => apiRequest('POST', '/turnos/entregas', datos),
    recibirEntrega: (id, datos) => apiRequest('PUT', `/turnos/entregas/${id}/recibir`, datos),
  },
};

export default api;
export { setToken, getToken, API_URL };
